import { useMemo, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import { ErrorBox } from "@/components/common";
import { AuthShell } from "@/components/AuthShell";
import { parseApiError } from "@/lib/api/client";
import { KeyRound, MailCheck, CheckCircle2 } from "lucide-react";

const API_BASE = (import.meta.env.VITE_API_BASE_URL || "/api/v1").replace(/\/+$/, "");

/** Two-in-one password reset surface.
 *
 * - With ``?uid=…&token=…`` (the link from the email) it shows the "choose a
 *   new password" form and POSTs to ``password/reset/confirm/``.
 * - Without those params it shows the "email me a reset link" form and POSTs
 *   to ``password/reset/request/`` (which always returns 200 — no account
 *   enumeration). */
export function ResetPassword() {
  const nav = useNavigate();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const uid = params.get("uid");
  const token = params.get("token");
  const hasLink = Boolean(uid && token);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);
  const [doneReset, setDoneReset] = useState(false);

  const passwordOk = password.length >= 12;

  async function requestLink(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/iam/password/reset/request/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!r.ok) {
        throw await parseApiError(r);
      }
      setSent(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  async function confirmReset(e: React.FormEvent) {
    e.preventDefault();
    if (!passwordOk) { setError("Password must be at least 12 characters."); return; }
    setBusy(true); setError(null);
    try {
      const r = await fetch(`${API_BASE}/iam/password/reset/confirm/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ uid, token, new_password: password }),
      });
      if (!r.ok) throw await parseApiError(r);
      setDoneReset(true);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally { setBusy(false); }
  }

  return (
    <AuthShell
      eyebrow="Account security"
      title={hasLink ? "Choose a new password" : "Reset your password"}
      subtitle="Reset links are single-use and time-bound. We never reveal whether an address has an account — your privacy is protected."
      bullets={[
        "Single-use, time-bound reset tokens",
        "All existing sessions are invalidated on reset",
        "No account enumeration — privacy by default",
      ]}
    >
      {!hasLink && !sent && (
        <form onSubmit={requestLink}>
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <MailCheck size={16} className="text-accent" /> Email me a reset link
          </div>
          <label className="label" htmlFor="rp-email">Account email</label>
          <input id="rp-email" className="input mb-3" type="email" autoFocus
                 value={email} onChange={(e) => setEmail(e.target.value)} />
          <button className="btn-primary w-full justify-center py-2.5" disabled={busy} type="submit">
            {busy ? "Sending…" : "Send reset link"}
          </button>
          {error && <div className="mt-3"><ErrorBox error={error} /></div>}
          <div className="mt-4 text-center text-xs text-muted">
            Remembered it?{" "}
            <Link to="/login" className="text-accent underline">Back to sign in</Link>
          </div>
        </form>
      )}

      {!hasLink && sent && (
        <div>
          <div className="mb-4 flex items-center gap-2 text-good">
            <CheckCircle2 size={20} /> <span className="font-medium">Check your inbox.</span>
          </div>
          <p className="text-sm text-muted">
            If an account exists for <strong>{email}</strong>, we've sent a reset
            link. It expires shortly — open it on this device to continue.
          </p>
          <div className="mt-5">
            <Link to="/login" className="btn">Back to sign in</Link>
          </div>
        </div>
      )}

      {hasLink && !doneReset && (
        <form onSubmit={confirmReset}>
          <div className="mb-3 flex items-center gap-2 text-sm font-medium">
            <KeyRound size={16} className="text-accent" /> New password
          </div>
          <label className="label" htmlFor="rp-pwd">New password</label>
          <input id="rp-pwd" className="input mb-1" type="password" autoFocus
                 value={password} onChange={(e) => setPassword(e.target.value)} />
          <div className={`mb-3 text-[11px] ${passwordOk ? "text-good" : "text-muted"}`}>
            {passwordOk ? "Strong enough" : "Min 12 characters"}
          </div>
          <button className="btn-primary w-full justify-center py-2.5" disabled={busy} type="submit">
            {busy ? "Updating…" : "Set new password"}
          </button>
          {error && <div className="mt-3"><ErrorBox error={error} /></div>}
        </form>
      )}

      {hasLink && doneReset && (
        <div>
          <div className="mb-4 flex items-center gap-2 text-good">
            <CheckCircle2 size={20} /> <span className="font-medium">Password updated.</span>
          </div>
          <p className="mb-5 text-sm text-muted">
            Your password has been changed and other sessions were signed out.
          </p>
          <button className="btn-primary w-full justify-center py-2.5"
                  onClick={() => nav({ to: "/login" })}>
            Sign in →
          </button>
        </div>
      )}
    </AuthShell>
  );
}
